import React, { FC, useCallback, useContext, useEffect, useState } from 'react';

import { GlobalContext } from '../../../../store';
import { IAdminEvent } from '../../../../store/admin/events/store';
import { ISeason } from '../../../../store/seasons/store';

import OverviewEvents from '../../Common/OverviewEvents/OverviewEvents';
import Button from '../../../../components/Button';
import { navigateTo } from '../../../../contrib/Utils/navigateTo';
import { useRouteInfoState } from '../../../../components/RouteInfo/Context/RouteInfo.context';
import FancyTabs, { Tab } from '../../../../components/FancyTabs';
import { sortSeasonsByYear } from '../../../../Utils/sort';

const EventsSection: FC = () => {
  const { clubId } = useRouteInfoState();

  const {
    actions: {
      seasons: { setCurrent }
    },
    adminEvents,
    seasons
  } = useContext(GlobalContext);

  const [clubEvents, setClubEvents] = useState<IAdminEvent[]>([]);

  const [eventsBySeason, setEventsBySeason] = useState<{ [year: string]: IAdminEvent[] }>();

  const [seasonList, setSeasonList] = useState<ISeason[]>([]);

  const [activeSeasonIndex, setActiveSeasonIndex] = useState<number>(0);

  const onTabChange = useCallback(
    async (tabIndex: number) => {
      if (clubId) {
        const newSeason = sortSeasonsByYear(seasons[clubId].seasons || [])[tabIndex];

        setCurrent(Number(clubId), newSeason);
      }
    },
    [seasons, clubId]
  );

  useEffect(() => {
    const currentClubSeason = seasons[String(clubId)] && seasons[String(clubId)].currentSeason;

    const newClubEvents = Object.values(adminEvents.all).filter(
      e =>
        e.club === Number(clubId) &&
        (!currentClubSeason || (e.season && e.season.id === (currentClubSeason as any).id))
    );

    if (newClubEvents.length !== clubEvents.length) {
      setClubEvents(newClubEvents);
    }
  }, [adminEvents, seasons, clubId]);

  useEffect(() => {
    if (clubId && seasons[clubId]) {
      setSeasonList(sortSeasonsByYear(seasons[clubId].seasons));
    }
  }, [clubId, seasons]);

  useEffect(() => {
    const newEventsBySeason: { [year: string]: IAdminEvent[] } = clubEvents
      .filter(e => !!e.season)
      .reduce(
        (prev: { [year: string]: IAdminEvent[] }, event) => ({
          ...prev,
          [event.season.year]: (prev[event.season.year] || []).concat(event)
        }),
        {}
      );

    setEventsBySeason(newEventsBySeason);
  }, [clubEvents]);

  useEffect(() => {
    if (clubId && seasons[clubId]) {
      const currentSeasonId = seasons[clubId].currentSeason.id;

      const newActiveSeasonIndex = seasonList.findIndex(
        season => Number(season.id) === Number(currentSeasonId)
      );

      setActiveSeasonIndex(newActiveSeasonIndex);
    }
  }, [clubId, seasons, seasonList]);

  return (
    <div className="events-section">
      <div className="admin-wrapper">
        {seasonList.length > 0 && seasonList[activeSeasonIndex] && (
          <FancyTabs
            outlinedAutoComplete={true}
            useAutoComplete={true}
            currentTab={activeSeasonIndex}
            menuTitle={seasonList[activeSeasonIndex].year}
            onChange={onTabChange}
            extraNavigationElement={
              <div className="section-buttons">
                <Button
                  size="medium"
                  variant="text"
                  color="primary"
                  type="button"
                  className="button section-button"
                  classes={{ label: 'section-button-label' }}
                  onClick={navigateTo(`/staff/club/${clubId}/event/new/setup/start`)}
                >
                  CREATE NEW EVENT
                </Button>

                {clubEvents.length > 0 && (
                  <Button
                    size="medium"
                    variant="text"
                    color="primary"
                    type="button"
                    className="button section-button"
                    classes={{ label: 'section-button-label' }}
                    onClick={navigateTo(`/staff/club/${clubId}/events`)}
                  >
                    View all events
                  </Button>
                )}
              </div>
            }
          >
            {seasonList.map(season => (
              <Tab key={season.year} title={season.year}>
                {!!eventsBySeason && eventsBySeason[season.year] ? (
                  <OverviewEvents events={eventsBySeason[season.year] || []} />
                ) : (
                  <></>
                )}
              </Tab>
            ))}
          </FancyTabs>
        )}
      </div>
    </div>
  );
};

export default EventsSection;
